import classNames from "classnames";
import {
  CSSProperties,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { Icon } from "../Icon";
import { PermissionContext } from "../../../components/Administrator/PermissionList";

import styles from "./Accordion.module.css";

export interface AccordionProps {
  title: string;
  description: string;
  icon?: string;
  details?: string;
  bodyClassName?: string;
  containerStyle?: CSSProperties;
  children: ReactNode;
  accordionKey?: string;
}

export default function Accordion({
  title,
  description,
  icon,
  details,
  bodyClassName,
  containerStyle,
  children,
  accordionKey,
}: Readonly<AccordionProps>) {
  const accordionsContextData = useContext(PermissionContext);
  const [isOpen, setIsOpen] = useState(false);
  const [shown, setShown] = useState(false);

  function onOpen() {
    setIsOpen(!isOpen);
    setTimeout(() => {
      setShown(!shown);
    }, 50);
  }

  // Toggle open all accordions (only when the toggle switch is clicked)
  useEffect(() => {
    if (accordionsContextData) {
      setIsOpen(accordionsContextData.showAll);
      setShown(accordionsContextData.showAll);
    }
  }, [accordionsContextData?.toggleTrigger]);

  // Get open state for each accordion and set switcher state
  useEffect(() => {
    if (accordionKey) {
      const newAccordionsState = accordionsContextData?.accordions || {};
      newAccordionsState[accordionKey] = isOpen;
      const allOpen = Object.values(newAccordionsState).every(Boolean);
      accordionsContextData?.setShowAll(allOpen);
    }
  }, [isOpen]);

  const bodyClassNames = classNames({
    [styles.body]: true,
    [styles.visibleBody]: shown,
    ...(bodyClassName ? { [bodyClassName]: true } : {}),
  });

  return (
    <div className={styles.container} style={containerStyle}>
      <button
        type="button"
        className={styles.header}
        onClick={(e) => {
          e.preventDefault();
          onOpen();
        }}
        aria-label={`${title}. ${description} - ${details}`}
        style={{ borderRadius: isOpen ? "4px 4px 0 0" : "4px" }}
      >
        <div className={styles.headerLeftSide}>
          {icon && (
            <div className={styles.iconWrapper}>
              <Icon name={icon} color="Primary-700" />
            </div>
          )}
          <div className={styles.mainGroupInfo}>
            <div className={styles.title}>{title}</div>
            <div className={styles.description}>{description}</div>
          </div>
        </div>
        <div className={styles.headerRightSide}>
          {details && <div className={styles.details}>{details}</div>}
          <div>
            {isOpen ? <Icon name="chevron-up" /> : <Icon name="chevron-down" />}
          </div>
        </div>
      </button>
      {isOpen && (
        <div className={bodyClassNames}>
          <div className={styles.innerBody}>{children}</div>
        </div>
      )}
    </div>
  );
}
